import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { useToast, Flex, Box } from '@withjoy/joykit';
import { noop } from 'lodash-es';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem } from '@withjoy/joykit/theme';
import { getAbsoluteUrlFromPathname } from '@shared/utils/urlHelpers';
import { useEmailVerificationDialog } from '@shared/components/EmailVerificationDialog/useEmailVerificationDialog';
import { usePublishCrossSell } from '@shared/components/StationeryCrossSell';
// Leaf module, not the barrel: the barrel also pulls in the provider and the lazy panel, which
// would put both into the bundle of all 12 publish surfaces that never open the wizard.
import { useGoLiveChecksContext } from '@shared/components/GoLiveChecks/GoLiveChecks.context';
import type { StationeryCrossSellTelemetrySurface } from '@shared/components/StationeryCrossSell';

interface UseUnpublishedWebsiteWarningParams {
  context: string | { category: string; page?: string };
  extraInfo?: Record<string, unknown>;
  /**
   * Opt in to the post-publish stationery cross-sell modal. Defaults to `false`
   * so incidental publishes triggered by send/share flows (messaging, reminders,
   * card/eCard post-checkout, etc.) keep their existing success toast and don't
   * interrupt those flows. Only deliberate "publish my website" surfaces (e.g.
   * the website-editor Publish button) should enable it.
   *
   * Ignored while the Go Live Checks wizard is active on this surface: its "Your site is live"
   * screen carries the share-and-cards prompt, so the modal would only repeat it. The success
   * toast is skipped there too, for the same reason.
   */
  enablePublishCrossSell?: boolean;
  /**
   * Opt in to the pre-publish "Go Live Checks" wizard. Only takes effect where a
   * `GoLiveChecksProvider` is mounted *and* `websiteGoLiveChecksEnabled` is on — today that is
   * the website editor alone.
   *
   * Deliberately opt-in per surface. The twelve incidental publishes (finishing a card, sending
   * a message, a reminder) are exactly the flows an eight-step wizard must not interrupt.
   */
  enableGoLiveChecks?: boolean;
  /**
   * Wraps the publish handler the Go Live Checks wizard runs. The website editor uses it to keep
   * the extra work it does on publish (poking the preview message bus) attached to the handler
   * the wizard invokes, rather than the panel reimplementing publish. Ignored when the wizard
   * isn't active.
   *
   * The wrapped handler resolves `true` only on a confirmed publish; pass that through.
   */
  composePublishHandler?: (publish: () => Promise<boolean>) => () => Promise<boolean>;
}

export const useUnpublishedWebsiteWarning = (params: UseUnpublishedWebsiteWarningParams) => {
  const { context, extraInfo = {}, enablePublishCrossSell = false, enableGoLiveChecks = false, composePublishHandler } = params;
  const { eventInfo, eventHandle } = useEventInfo();
  const eventId = eventInfo?.eventId || '';
  const { tq } = useTranslation('websiteDesigner');
  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const promiseResolveRef = useRef(noop);
  const { toast } = useToast();
  const analytics = useContext(AnalyticsContext);
  const { ensureEmailVerified, dialogProps: emailVerificationDialogProps } = useEmailVerificationDialog();
  // `undefined` everywhere but the website editor, and a plain context read where it is mounted —
  // so the other publish surfaces pay nothing for a wizard they never open.
  const goLiveChecks = useGoLiveChecksContext();
  const isGoLiveChecksActive = enableGoLiveChecks && !!goLiveChecks?.isEnabled;
  // The publish handler the wizard runs. `useEventCallback` keeps one stable identity while always
  // invoking the latest closure, so `openUnpublishedWebsiteWarning` — declared above
  // `handlePublishClick` — doesn't churn all 13 call sites every time that handler's deps change.
  const runWizardPublish = useEventCallback(async () => {
    const publish = composePublishHandler ? composePublishHandler(handlePublishClick) : handlePublishClick;
    return publish();
  });
  // Cross-sell events report against the calling page — same context this hook
  // already uses for its PublishGoLive event. Only admin publish surfaces opt in.
  const contextCategory = typeof context === 'string' ? context : context.category;
  const contextPage = typeof context === 'string' ? undefined : context.page;
  const crossSellSurface = useMemo<StationeryCrossSellTelemetrySurface>(
    () => ({ category: contextCategory, pagePrefix: 'admin', page: contextPage }),
    [contextCategory, contextPage],
  );
  const { handlePublishSuccess, dialogProps: crossSellDialogProps } = usePublishCrossSell(crossSellSurface);

  const [isWarningOpen, setIsWarningOpen] = useState(false);

  useEffect(() => {
    return () => {
      promiseResolveRef.current();
      promiseResolveRef.current = noop;
    };
  }, []);

  /**
   * Opens the unpublished website warning modal if the current event
   * is eligible for website exposure but hasn't been published yet.
   *
   * Returns a Promise that resolves when the user takes action
   * (either to publish or keep hidden). This allows calling code to
   * await the user's choice before proceeding (e.g., pausing a share or send flow).
   */
  const openUnpublishedWebsiteWarning = useCallback(async () => {
    const isWebsiteExposed = websitePublishCapabilityFeature.eventExposure.value;
    const isWebsitePublished = websitePublishCapabilityFeature.eventPublishedState.value;

    // If feature is enabled and the website isn't published, prompt the user.
    if (isWebsiteExposed && !isWebsitePublished) {
      // The wizard replaces the warning modal rather than layering on it — it opens instead, and
      // `isWarningOpen` deliberately stays false so the two publish UIs can never be on screen
      // together. It resolves on the same terms as the modal: when the session ends, whether that
      // was a publish or a dismissal, so an awaiting share flow still waits for the couple.
      //
      // No `ensureEmailVerified()` ahead of it: verification is the wizard's own first step, which
      // blocks — it offers no publish action until the address is verified — so the gate is kept,
      // in the wizard's own UI instead of a modal stacked in front of it.
      if (isGoLiveChecksActive && goLiveChecks) {
        return new Promise<void>(resolve => {
          promiseResolveRef.current = resolve;
          goLiveChecks.open({
            onPublish: runWizardPublish,
            onClose: () => {
              promiseResolveRef.current();
              promiseResolveRef.current = noop;
            },
          });
        });
      }

      // Gate publishing behind email verification before showing the publish dialog.
      // If the user dismisses verification, treat it like "keep hidden" and continue.
      const verified = await ensureEmailVerified();
      if (!verified) {
        return;
      }

      // Return a promise that will resolve when the user closes/interacts with the warning.
      const promise = new Promise(resolve => {
        promiseResolveRef.current = resolve;
      });

      setIsWarningOpen(true);

      return promise;
    }

    // Otherwise, resolve immediately (no warning needs to be shown).
    return Promise.resolve();
  }, [
    websitePublishCapabilityFeature.eventExposure.value,
    websitePublishCapabilityFeature.eventPublishedState.value,
    ensureEmailVerified,
    isGoLiveChecksActive,
    goLiveChecks,
    runWizardPublish,
  ]);

  // Resolves whether the site actually went live. Publish failures are handled here (logged and
  // toasted) rather than thrown, so callers that care — the Go Live Checks wizard, which records a
  // terminal outcome — need the result reported instead.
  const handlePublishClick = useCallback(async () => {
    try {
      await websitePublishCapabilityFeature.eventPublishedState.update(true);
      // Under Go Live Checks the wizard's "Your site is live" screen is the
      // confirmation, so neither the cross-sell nor the toast shows over it.
      // Otherwise show the cross-sell when this surface opted in (deliberate
      // website publishes), and fall back to the standard "site published"
      // toast. Keeps send/checkout flows uninterrupted.
      if (!isGoLiveChecksActive && !(enablePublishCrossSell && handlePublishSuccess())) {
        toast(
          <Flex
            alignItems="center"
            gap={5}
          >
            {tq`🥳 Your site has been published.`}
            <Box
              as="a"
              href={getAbsoluteUrlFromPathname(`/${eventHandle}`)}
              target="_blank"
              rel="noopener noreferrer"
              typographyVariant="label4"
              textDecoration="underline"
              textDecorationStyle="dotted"
              fontWeight={500}
              style={{
                textUnderlineOffset: pxToRem(2),
              }}
            >
              {tq`View Your Site →`}
            </Box>
          </Flex>,
        );
      }

      analytics.track({
        category: typeof context === 'string' ? context : context.category,
        action: 'PublishGoLive',
        extraInfo: {
          eventId,
          ...extraInfo,
        },
      });

      // The warning modal is done once it publishes, so its opener resumes now. The wizard is not:
      // it stays open on "Your site is live", and a share flow awaiting the opener must not resume
      // underneath it. Its session's `onClose` resolves the promise when the couple leaves.
      if (!isGoLiveChecksActive) {
        promiseResolveRef.current();
        promiseResolveRef.current = noop;
        setIsWarningOpen(false);
      }
      return true;
    } catch (error) {
      addError(error, { context: 'useUnpublishedWebsiteWarning#handlePublishClick' });
      toast(tq`😬 Failed to publish website. Please try again.`);
      return false;
    }
  }, [
    websitePublishCapabilityFeature.eventPublishedState,
    enablePublishCrossSell,
    isGoLiveChecksActive,
    handlePublishSuccess,
    toast,
    tq,
    eventHandle,
    analytics,
    context,
    eventId,
    extraInfo,
  ]);

  const handleKeepHiddenClick = useCallback(() => {
    promiseResolveRef.current();
    promiseResolveRef.current = noop;
    setIsWarningOpen(false);
  }, []);

  const warningProps = useMemo(
    () => ({
      isOpen: isWarningOpen,
      onPublish: handlePublishClick,
      isPublishing: websitePublishCapabilityFeature.eventPublishedState.isUpdating,
      onKeepHidden: handleKeepHiddenClick,
      emailVerificationDialogProps,
      crossSellDialogProps,
    }),
    [isWarningOpen, handlePublishClick, handleKeepHiddenClick, websitePublishCapabilityFeature.eventPublishedState.isUpdating, emailVerificationDialogProps, crossSellDialogProps],
  );

  return {
    openUnpublishedWebsiteWarning,
    warningProps,
  };
};
